import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconArrowUp, IconChevronDown, IconClockHour4, IconSparkles } from "@tabler/icons-react";

import { Text, Button, Menu } from "@fragments";

import "./styles.scss";

const MAX_INPUT_HEIGHT = 240;

/**
 * The prompt composer: a multiline field with the two settings — target
 * duration and personality, each a dropdown button reading
 * "label: value ▾" — and the send action in a toolbar under it.
 *
 * While a request is in flight the box keeps exactly its size: the settings
 * stay visible but disabled, the send button shows its loading state and
 * the field is read-only.
 */
export default function ScriptComposer({
    value,
    onChange,
    duration,
    durations,
    onDurationChange,
    personality,
    personalities,
    onPersonalityChange,
    generating,
    followUp,
    onGenerate,
}) {
    const { t } = useTranslation("contentModals");
    const inputRef = useRef(null);

    // Auto-grow: the field follows its content up to a cap, then scrolls.
    // Frozen while generating — the message just left the field, and the
    // box must not shrink while the answer is being written.
    useEffect(() => {
        const el = inputRef.current;
        if (!el || generating) return;

        el.style.height = "auto";
        const next = Math.min(el.scrollHeight, MAX_INPUT_HEIGHT);
        el.style.height = `${next}px`;
        el.style.overflowY = el.scrollHeight > MAX_INPUT_HEIGHT ? "auto" : "hidden";
    }, [value, generating]);

    function handleKeyDown(event) {
        if (event.key !== "Enter" || event.shiftKey || event.nativeEvent?.isComposing) return;
        event.preventDefault();
        if (!generating) onGenerate();
    }

    const canSend = value.trim().length > 0;

    const durationItems = durations.map((minutes) => ({
        id: `duration-${minutes}`,
        label: t("generateScript.minutes", { count: minutes }),
        active: minutes === duration,
        onClick: () => onDurationChange(minutes),
    }));

    const current = personalities.find((option) => option.key === personality) || personalities[0];
    const personalityItems = personalities.map(({ key, Icon }) => ({
        id: key,
        label: t(`generateScript.personality.options.${key}.label`),
        icon: <Icon size={16} stroke={1.75} />,
        active: key === personality,
        onClick: () => onPersonalityChange(key),
    }));

    // "Duração: 5 min" — the label muted, the value strong, in one button.
    const setting = (label, valueText) => (
        <>
            <Text.span contrast={6} className="script-composer__setting-label">{label}:</Text.span>
            <Text.span contrast={9} weight={500} className="script-composer__setting-value">{valueText}</Text.span>
        </>
    );

    return (
        <div className={`script-composer ${generating ? "is-generating" : ""}`.trim()}>
            <textarea
                ref={inputRef}
                className="script-composer__input"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={followUp ? t("generateScript.followUpPlaceholder") : t("generateScript.promptPlaceholder")}
                aria-label={t("generateScript.promptLabel")}
                rows={3}
                readOnly={generating}
                spellCheck
            />

            <div className="script-composer__toolbar">
                <div className="script-composer__settings">
                    <Menu activateOnHover={true} items={durationItems} placement="top-center">
                        <Button
                            size="small"
                            variant="text"
                            color="text"
                            startIcon={<IconClockHour4 />}
                            endIcon={<IconChevronDown />}
                            disabled={generating}
                            preventLoading
                            className="script-composer__setting"
                            aria-label={t("generateScript.durationLabel")}
                        >
                            {setting(t("generateScript.durationShort"), t("generateScript.minutes", { count: duration }))}
                        </Button>
                    </Menu>

                    <Menu activateOnHover={true} items={personalityItems} placement="top-center">
                        <Button
                            size="small"
                            variant="text"
                            color="text"
                            startIcon={<current.Icon />}
                            endIcon={<IconChevronDown />}
                            disabled={generating}
                            preventLoading
                            className="script-composer__setting"
                            aria-label={t("generateScript.personality.label")}
                        >
                            {setting(t("generateScript.personality.label"), t(`generateScript.personality.options.${current.key}.label`))}
                        </Button>
                    </Menu>
                </div>

                {/* Never `disabled` while idle: an empty send shows the "too
                    short" hint under the box instead. */}
                <Button
                    variant="rounded"
                    color="inverse"
                    startIcon={followUp ? <IconArrowUp /> : <IconSparkles />}
                    onClick={onGenerate}
                    loading={generating}
                    preventLoading
                    className={`script-composer__submit ${canSend || generating ? "" : "is-idle"}`.trim()}
                    tooltip={generating ? undefined : t("generateScript.shortcut")}
                >
                    {generating
                        ? t("generateScript.generating")
                        : followUp ? t("generateScript.send") : t("generateScript.generate")}
                </Button>
            </div>
        </div>
    );
}
